import { useCallback } from 'react';
import { useCreateNewProject, useMigrateProject } from '.';
import {
  hideLoadingScreen,
  setIsProjectSaved,
  setSkipSpellcheckValidation,
  showLoadingScreen,
  useAppContext,
} from '../../../context/app-context';
import { ErrorResponse } from '../../../global-types';
import { useQueryParams, useSanitizeSearchUrl, useSystemErrorHandling } from '../../../hooks';
import { useMembershipData } from '../../../hooks/use-membership-data';
import { getProductId, getProductTypeCode, getProjectId, getProjectName, isLoggedIn } from '../../../utils';
import { getInitializationRequestBody } from '../utils/get-intialization-request-body';
import { useLoadExistingProject } from './use-load-existing-project';

/**
 *
 * @param isDataLoaded - Ref that is used to track when the card data has loaded.
 * @param dependenciesList - List of dependencies to be passed to the survey.
 * @param isOneToMany - Boolean that indicates if the project is a 1:Many project
 * @returns
 */
export const useStartProject = (
  isDataLoaded: React.MutableRefObject<boolean>,
  dependenciesList,
  isOneToMany: boolean,
): (() => Promise<void>) => {
  // Context Hooks
  const {
    appState: { productQuantity },
    appDispatch,
  } = useAppContext();

  // Utility Hooks
  const { onSystemError } = useSystemErrorHandling();
  const { isSearchUrlSanitized } = useSanitizeSearchUrl();
  const queryParams = useQueryParams();
  const projectId = getProjectId();

  const productTypeCode = getProductTypeCode();
  const projectName = getProjectName();

  const createNewProject = useCreateNewProject();
  const migrateProject = useMigrateProject();
  const { loadExistingProject } = useLoadExistingProject();
  const { loadMembershipData } = useMembershipData();

  const startProject = useCallback(async () => {
    if (!isSearchUrlSanitized) {
      return;
    }

    const migrateFromId = queryParams.get('migrateFrom');
    // Every time the project starts we set this to false
    setIsProjectSaved(appDispatch, false);
    try {
      if (isLoggedIn()) {
        loadMembershipData();
      }

      showLoadingScreen(appDispatch, '');
      if (!projectId) {
        // If project doesn't exist, create a new project.
        const initializationRequestBody = getInitializationRequestBody({
          projectName,
          productId: getProductId(),
          productTypeCode,
          isLoggedIn: isLoggedIn(),
          productQuantity: productQuantity,
          isOneToMany,
        });

        await createNewProject(initializationRequestBody, isDataLoaded);
        return;
      }
      // When user returns to an existing project, ensure spellcheck validation is always enabled the first time
      setSkipSpellcheckValidation(appDispatch, false);
      if (migrateFromId) {
        migrateProject(migrateFromId, projectId);
      }

      loadExistingProject(projectId, isDataLoaded);
    } catch (errors) {
      hideLoadingScreen(appDispatch);
      const redirectDialog = (errors as ErrorResponse)?.status === 401;
      onSystemError(errors as ErrorResponse, { redirect: redirectDialog });
    }
  }, dependenciesList);

  return startProject;
};
